<template>
	<div class="auto-campaign-sequences-wrapper">
		<div class="auto-campaign-sequences-page">
			<div class="create-campaign-container container">
				<!-- Header -->
				<div class="page-header d-flex align-items-center my-4">
					<router-link :to="{ name: 'createAutoCampaign' }" class="back-btn me-3" title="Back to Campaign Options">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">Create Auto Campaign with Sequences</h2>
						<p class="page-subtitle">Set up your automated email sequences and scheduling.</p>
					</div>
				</div>

				<!-- Main Content -->
				<div class="main-content-wrapper">
					<!-- Timeline Section -->
					<div class="timeline-section" :class="{ 'timeline-expanded': !showConfig }">
						<div class="timeline-container" :class="{ 'timeline-centered': !showConfig }">
							<!-- Timeline -->
							<div class="sequence-timeline">
								<!-- Start Trigger -->
								<div class="timeline-start">
									<div class="timeline-trigger" @click="selectCampaignInfo" :class="{ active: selectedItemId === 'campaign' }">
										<i class="fad fa-play"></i>
									</div>
									<div class="timeline-start-info">
										<div class="start-campaign-name">{{ campaign.name || 'Untitled Campaign' }}</div>
										<div class="start-campaign-description">{{ campaign.description || 'Click to edit campaign details' }}</div>
									</div>
									<div class="timeline-connector"></div>
								</div>

								<!-- Timeline Items -->
								<div class="timeline-items">
									<div
										v-for="(item, index) in sequenceItems"
										:key="item.id"
										class="timeline-item"
										:class="{ active: selectedItemId === item.id }"
										@click="selectItem(item.id)"
									>
										<!-- Timeline Card -->
										<div class="timeline-sequence-card" :class="getItemTypeClass(item.type)">
											<div class="card-icon">
												<i v-if="item.type === 'email'" class="fad fa-envelope"></i>
												<i v-else-if="item.type === 'postcard'" class="fad fa-image"></i>
												<i v-else-if="item.type === 'letter'" class="fad fa-file-pdf"></i>
											</div>
											<div class="card-content">
												<div class="card-title">
													{{ item.name || `${getItemTypeLabel(item.type)}` }}
												</div>
												<div class="card-subtitle">Date</div>
											</div>
											<div class="card-actions">
												<button
													class="btn btn-xs btn-outline-secondary card-action-btn"
													@click.stop="moveItem(index, -1)"
													:disabled="index === 0"
													title="Move Up"
												>
													<i class="fad fa-chevron-up"></i>
												</button>
												<button
													class="btn btn-xs btn-outline-secondary card-action-btn"
													@click.stop="moveItem(index, 1)"
													:disabled="index === sequenceItems.length - 1"
													title="Move Down"
												>
													<i class="fad fa-chevron-down"></i>
												</button>
												<button class="btn btn-xs btn-outline-danger card-action-btn" @click.stop="removeItem(index)" title="Remove">
													<i class="fad fa-trash"></i>
												</button>
											</div>
										</div>

										<!-- Connector Line -->
										<div v-if="index < sequenceItems.length - 1 || sequenceItems.length > 0" class="timeline-connector"></div>
									</div>
								</div>

								<!-- Add Step Button -->
								<div class="timeline-add-item">
									<div class="add-step-circle" @click="toggleAddDropdown" :class="{ active: showAddDropdown }">
										<i class="fad" :class="showAddDropdown ? 'fa-minus' : 'fa-plus'"></i>
									</div>

									<!-- Add Item Dropdown -->
									<div v-if="showAddDropdown" class="add-item-dropdown">
										<div class="dropdown-options">
											<button class="dropdown-option" @click="addSequenceItem('email')">
												<i class="fad fa-envelope me-2"></i>
												Email
											</button>
											<button class="dropdown-option" @click="addSequenceItem('postcard')">
												<i class="fad fa-image me-2"></i>
												Postcard
											</button>
											<button class="dropdown-option" @click="addSequenceItem('letter')">
												<i class="fad fa-file-pdf me-2"></i>
												Letter
											</button>
										</div>
									</div>

									<!-- Final Connector -->
									<div v-if="sequenceItems.length > 0" class="timeline-connector"></div>
								</div>

								<!-- End Node -->
								<div v-if="sequenceItems.length > 0" class="timeline-end">
									<div class="timeline-end-circle">
										<span>End</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Right Side - Configuration -->
					<div class="config-section" :class="{ 'config-visible': showConfig }">
						<div class="item-configuration-container">
							<div v-if="!selectedItemId" class="no-selection">
								<div class="no-selection-content">
									<i class="fad fa-mouse-pointer fa-3x text-muted mb-3"></i>
									<h5 class="text-muted">Select an Item</h5>
									<p class="text-muted">Choose a campaign or sequence item from the timeline to configure its settings</p>
								</div>
							</div>

							<!-- Campaign Configuration -->
							<div v-else-if="selectedItemId === 'campaign'" class="item-config">
								<div class="config-header">
									<h4>Configure Campaign</h4>
									<div class="config-actions">
										<div class="item-type-badge campaign-badge">
											<i class="fad fa-play me-1"></i>
											Campaign
										</div>
									</div>
								</div>

								<div class="config-form">
									<div class="mb-3">
										<label class="form-label">Campaign Name</label>
										<input type="text" class="form-control" v-model="campaign.name" placeholder="Enter campaign name" />
										<small class="form-text text-muted">
											This name will appear in the timeline and campaign list
										</small>
									</div>

									<div class="mb-3">
										<label class="form-label">Description</label>
										<textarea class="form-control" rows="3" v-model="campaign.description" placeholder="Describe your sequence campaign"></textarea>
									</div>

									<div class="config-placeholder">
										<p class="text-muted">Additional campaign configuration options will appear here.</p>
									</div>
								</div>
							</div>

							<!-- Sequence Item Configuration -->
							<div v-else class="item-config">
								<div class="config-header">
									<h4>Configure {{ getSelectedItem() ? getItemTypeLabel(getSelectedItem().type) : 'Item' }}</h4>
									<div class="config-actions">
										<div class="item-type-badge" v-if="getSelectedItem()" :class="getItemTypeClass(getSelectedItem().type)">
											<i v-if="getSelectedItem().type === 'email'" class="fad fa-envelope me-1"></i>
											<i v-else-if="getSelectedItem().type === 'postcard'" class="fad fa-image me-1"></i>
											<i v-else-if="getSelectedItem().type === 'letter'" class="fad fa-file-pdf me-1"></i>
											{{ getItemTypeLabel(getSelectedItem().type) }}
										</div>
									</div>
								</div>

								<div class="config-form" v-if="getSelectedItem()">
									<div class="mb-3">
										<label class="form-label">Step Name</label>
										<input type="text" class="form-control" v-model="getSelectedItem().name" :placeholder="`Enter name for this ${getSelectedItem().type}`" />
										<small class="form-text text-muted">
											This name will appear in the timeline
										</small>
									</div>

									<div class="mb-3">
										<label class="form-label">Description</label>
										<textarea
											class="form-control"
											rows="3"
											v-model="getSelectedItem().description"
											:placeholder="`Describe this ${getSelectedItem().type} step`"
										></textarea>
									</div>

									<!-- Letter Configuration -->
									<div v-if="getSelectedItem().type === 'letter'">
										<!-- Letter Content Type -->
										<div class="mb-3">
											<label class="form-label">Content Type</label>
											<div class="btn-group w-100" role="group">
												<input
													type="radio"
													class="btn-check"
													:name="`letterContentType${getSelectedItem().id}`"
													:id="`contentTypePdf${getSelectedItem().id}`"
													value="pdf"
													v-model="getSelectedItem().config.letter_content_type"
												/>
												<label class="btn btn-outline-secondary" :for="`contentTypePdf${getSelectedItem().id}`">
													<i class="fad fa-file-pdf me-2"></i>
													Upload PDF
												</label>
												<input
													type="radio"
													class="btn-check"
													:name="`letterContentType${getSelectedItem().id}`"
													:id="`contentTypeHtml${getSelectedItem().id}`"
													value="html"
													v-model="getSelectedItem().config.letter_content_type"
												/>
												<label class="btn btn-outline-secondary" :for="`contentTypeHtml${getSelectedItem().id}`">
													<i class="fad fa-code me-2"></i>
													HTML Editor
												</label>
											</div>
										</div>

										<!-- PDF Upload Section -->
										<div v-if="getSelectedItem().config.letter_content_type === 'pdf'" class="mb-3">
											<label class="form-label">Letter PDF</label>
											<div class="pdf-upload-section">
												<div v-if="getSelectedItem().config.letter_pdf" class="uploaded-file-info">
													<i class="fad fa-file-pdf me-2 text-danger"></i>
													<span>{{ getSelectedItem().config.letter_pdf_name }}</span>
													<button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="removeLetterPdf(getSelectedItem())">
														<i class="fad fa-trash"></i>
													</button>
												</div>
												<div v-else class="upload-placeholder" @click="triggerLetterPdfUpload(getSelectedItem())">
													<i class="fad fa-cloud-upload me-2"></i>
													Click to upload PDF
												</div>
											</div>
										</div>

										<!-- Letter Settings -->
										<div class="mb-3">
											<label for="letterSender" class="form-label">Campaign Sender <span class="text-danger">*</span></label>
											<select class="form-select" v-model="getSelectedItem().config.sender">
												<option value="">Select Sender</option>
												<option v-for="sender in senders" :key="sender.name" :value="sender.name">
													{{ sender.name }}
												</option>
											</select>
										</div>

										<div class="row mb-3">
											<div class="col-6">
												<label class="form-label">Color <span class="text-danger">*</span></label>
												<select class="form-select" v-model="getSelectedItem().config.letter_color">
													<option value="">Select</option>
													<option value="1">Yes</option>
													<option value="0">No</option>
												</select>
											</div>
											<div class="col-6">
												<label class="form-label">Double sided <span class="text-danger">*</span></label>
												<select class="form-select" v-model="getSelectedItem().config.letter_double_sided">
													<option value="">Select</option>
													<option value="1">Yes</option>
													<option value="0">No</option>
												</select>
											</div>
										</div>

										<div class="mb-3">
											<label class="form-label">Extra service</label>
											<select class="form-select" v-model="getSelectedItem().config.letter_extra_service">
												<option value="">None</option>
												<option value="certified">Certified</option>
												<option value="certified_return_receipt">Certified return receipt</option>
											</select>
										</div>

										<div class="mb-3">
											<label class="form-label">Address placement <span class="text-danger">*</span></label>
											<select class="form-select" v-model="getSelectedItem().config.letter_address_placement">
												<option value="">Select placement</option>
												<option value="top_first_page">On top of first page</option>
												<option value="insert_blank_page">On a blank page</option>
											</select>
										</div>

										<div class="row mb-3">
											<div class="col-6">
												<label class="form-label">Letter Type</label>
												<select class="form-select" v-model="getSelectedItem().config.letter_type">
													<option value="default">Default</option>
													<option value="single-item-pattern">Single Item Pattern</option>
												</select>
											</div>
											<div class="col-6">
												<label class="form-label">Letter Pages Count</label>
												<input type="number" class="form-control" v-model="getSelectedItem().config.letter_pages_count" min="1" max="10" />
												<small class="form-text text-muted">Just for calculation</small>
											</div>
										</div>
									</div>

									<!-- Postcard Configuration -->
									<div v-else-if="getSelectedItem().type === 'postcard'">
										<!-- Postcard Front Image -->
										<div class="mb-3">
											<label class="form-label">Front Image</label>
											<div class="d-flex justify-content-between align-items-center mb-2">
												<small class="text-muted">{{ getSelectedItem().config.postcard_size || '4x6' }}</small>
												<div class="form-check form-switch">
													<input class="form-check-input" type="checkbox" v-model="getSelectedItem().config.front_is_template" />
													<label class="form-check-label">Template Image</label>
												</div>
											</div>
											<div class="postcard-image-upload">
												<div v-if="getSelectedItem().config.postcard_front" class="uploaded-image-preview">
													<img :src="getSelectedItem().config.postcard_front" alt="Front Image" class="img-preview" />
													<button type="button" class="btn btn-sm btn-outline-danger" @click="removePostcardImage(getSelectedItem(), 'front')">
														<i class="fad fa-trash"></i>
													</button>
												</div>
												<div v-else class="upload-placeholder" @click="triggerPostcardUpload(getSelectedItem(), 'front')">
													<i class="fad fa-image me-2"></i>
													Click to upload front image
												</div>
											</div>
										</div>

										<!-- Postcard Back Image -->
										<div class="mb-3">
											<label class="form-label">Back Image</label>
											<div class="d-flex justify-content-between align-items-center mb-2">
												<small class="text-muted">{{ getSelectedItem().config.postcard_size || '4x6' }}</small>
												<div class="form-check form-switch">
													<input class="form-check-input" type="checkbox" v-model="getSelectedItem().config.back_is_template" />
													<label class="form-check-label">Template Image</label>
												</div>
											</div>
											<div class="postcard-image-upload">
												<div v-if="getSelectedItem().config.postcard_back" class="uploaded-image-preview">
													<img :src="getSelectedItem().config.postcard_back" alt="Back Image" class="img-preview" />
													<button type="button" class="btn btn-sm btn-outline-danger" @click="removePostcardImage(getSelectedItem(), 'back')">
														<i class="fad fa-trash"></i>
													</button>
												</div>
												<div v-else class="upload-placeholder" @click="triggerPostcardUpload(getSelectedItem(), 'back')">
													<i class="fad fa-image me-2"></i>
													Click to upload back image
												</div>
											</div>
										</div>

										<!-- Postcard Settings -->
										<div class="row mb-3">
											<div class="col-6">
												<label class="form-label">Size <span class="text-danger">*</span></label>
												<select class="form-select" v-model="getSelectedItem().config.postcard_size">
													<option value="">Select Size</option>
													<option value="4x6">4x6</option>
													<option value="6x9">6x9</option>
													<option value="6x11">6x11</option>
												</select>
											</div>
											<div class="col-6">
												<label class="form-label">Campaign Sender <span class="text-danger">*</span></label>
												<select class="form-select" v-model="getSelectedItem().config.sender">
													<option value="">Select Sender</option>
													<option v-for="sender in senders" :key="sender.name" :value="sender.name">
														{{ sender.name }}
													</option>
												</select>
											</div>
										</div>
									</div>

									<!-- Email Configuration (simplified) -->
									<div v-else-if="getSelectedItem().type === 'email'" class="config-placeholder">
										<p class="text-muted">Email configuration options will appear here.</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AutoCampaignWithSequencesCreate',
	data() {
		return {
			campaign: {
				name: '',
				description: '',
				has_sequences: true,
				mode: 'test',
				mail_type: '',
				sequences: [],
			},
			sequenceItems: [],
			selectedItemId: null,
			showAddDropdown: false,
			showConfig: false,
			nextItemId: 1,
			senders: [{ name: 'Company Default' }, { name: 'Custom Sender' }],
		}
	},
	methods: {
		toggleAddDropdown() {
			this.showAddDropdown = !this.showAddDropdown
		},
		addSequenceItem(type) {
			const newItem = {
				id: this.nextItemId++,
				type: type,
				name: '',
				description: '',
				delay: null,
				status: 'draft',
				config:
					type === 'letter'
						? {
								// Letter configuration matching triggers edit
								letter_content_type: 'pdf',
								letter_pdf: null,
								letter_pdf_name: '',
								letter_html: '',
								sender: '',
								letter_color: '',
								letter_double_sided: '',
								letter_extra_service: '',
								letter_address_placement: '',
								letter_type: 'default',
								letter_pages_count: 1,
						}
						: type === 'postcard'
						? {
								// Postcard configuration matching triggers edit
								postcard_size: '4x6',
								postcard_front: null,
								postcard_back: null,
								front_is_template: false,
								back_is_template: false,
								sender: '',
						}
						: {
								// Email configuration (placeholder)
						},
			}

			this.sequenceItems.push(newItem)
			// Store in campaign sequences array
			this.campaign.sequences = [...this.sequenceItems]
			this.selectItem(newItem.id)
			this.showAddDropdown = false
		},
		selectItem(itemId) {
			if (this.selectedItemId === itemId && this.showConfig) {
				// Toggle off if clicking the same item
				this.showConfig = false
				this.selectedItemId = null
			} else {
				// Select new item or turn on if different item
				this.selectedItemId = itemId
				this.showConfig = true
			}
		},
		selectCampaignInfo() {
			if (this.selectedItemId === 'campaign' && this.showConfig) {
				// Toggle off if clicking the same campaign
				this.showConfig = false
				this.selectedItemId = null
			} else {
				// Select campaign
				this.selectedItemId = 'campaign'
				this.showConfig = true
			}
		},
		getSelectedItem() {
			return this.sequenceItems.find(item => item.id === this.selectedItemId)
		},
		removeItem(index) {
			const removedItem = this.sequenceItems[index]
			this.sequenceItems.splice(index, 1)

			// If the removed item was selected, clear selection or select another item
			if (this.selectedItemId === removedItem.id) {
				if (this.sequenceItems.length > 0) {
					// Select the item at the same index, or the last item if index is out of bounds
					const newIndex = Math.min(index, this.sequenceItems.length - 1)
					const newSelectedItem = this.sequenceItems[newIndex]
					this.selectedItemId = newSelectedItem ? newSelectedItem.id : null
				} else {
					this.selectedItemId = null
				}
			}
		},
		moveItem(index, direction) {
			const newIndex = index + direction
			if (newIndex < 0 || newIndex >= this.sequenceItems.length) return

			// Swap items using Vue.js array methods for reactivity
			const items = [...this.sequenceItems]
			const temp = items[index]
			items[index] = items[newIndex]
			items[newIndex] = temp
			this.sequenceItems = items
		},
		formatDelay(delay) {
			if (!delay) return 'Immediate'
			if (delay.unit === 'minutes') return `${delay.value} min`
			if (delay.unit === 'hours') return `${delay.value} hr`
			if (delay.unit === 'days') return `${delay.value} day${delay.value > 1 ? 's' : ''}`
			if (delay.unit === 'weeks') return `${delay.value} week${delay.value > 1 ? 's' : ''}`
			return `${delay.value} ${delay.unit}`
		},
		getItemTypeClass(type) {
			const classes = {
				email: 'email-node',
				postcard: 'postcard-node',
				letter: 'letter-node',
			}
			return classes[type] || ''
		},
		getItemTypeLabel(type) {
			const labels = {
				email: 'Email',
				postcard: 'Postcard',
				letter: 'Letter',
			}
			return labels[type] || type
		},
		saveCampaign() {
			// Update campaign sequences before saving
			this.campaign.sequences = [...this.sequenceItems]
			console.log('Saving sequence campaign:', {
				campaign: this.campaign,
				sequenceItems: this.sequenceItems,
			})
			// TODO: Implement actual save functionality
		},
		// File upload methods
		triggerLetterPdfUpload(item) {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.pdf'
			input.onchange = e => this.handleLetterPdfUpload(item, e)
			input.click()
		},
		handleLetterPdfUpload(item, event) {
			const file = event.target.files[0]
			if (file) {
				item.config.letter_pdf = URL.createObjectURL(file)
				item.config.letter_pdf_name = file.name
				// Update campaign sequences
				this.campaign.sequences = [...this.sequenceItems]
			}
		},
		removeLetterPdf(item) {
			item.config.letter_pdf = null
			item.config.letter_pdf_name = ''
			this.campaign.sequences = [...this.sequenceItems]
		},
		triggerPostcardUpload(item, side) {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = 'image/*'
			input.onchange = e => this.handlePostcardUpload(item, side, e)
			input.click()
		},
		handlePostcardUpload(item, side, event) {
			const file = event.target.files[0]
			if (file) {
				const imageUrl = URL.createObjectURL(file)
				if (side === 'front') {
					item.config.postcard_front = imageUrl
				} else {
					item.config.postcard_back = imageUrl
				}
				// Update campaign sequences
				this.campaign.sequences = [...this.sequenceItems]
			}
		},
		removePostcardImage(item, side) {
			if (side === 'front') {
				item.config.postcard_front = null
			} else {
				item.config.postcard_back = null
			}
			this.campaign.sequences = [...this.sequenceItems]
		},
	},
	mounted() {
		// Close dropdown when clicking outside
		document.addEventListener('click', e => {
			if (!this.$el.contains(e.target)) {
				this.showAddDropdown = false
			}
		})
	},
}
</script>

<style scoped>
.auto-campaign-sequences-wrapper {
	min-height: 100vh;
	background-color: #f8f9fa;
}

.auto-campaign-sequences-page {
	padding: 20px 0;
}

.back-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 1px solid #dee2e6;
	color: #6c757d;
	text-decoration: none;
	transition: all 0.2s ease;
}

.back-btn:hover {
	background-color: #f8f9fa;
	color: #495057;
	border-color: #adb5bd;
}

.page-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #212529;
	margin-bottom: 0.25rem;
}

.page-subtitle {
	color: #6c757d;
	margin-bottom: 0;
	font-size: 0.95rem;
}

.section-title {
	font-size: 1.1rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 16px;
}

.form-label {
	font-weight: 500;
	color: #374151;
	margin-bottom: 6px;
}

.form-control {
	border-radius: 8px;
	border: 1px solid #d1d5db;
	padding: 10px 14px;
	font-size: 0.95rem;
}

.form-control:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Main Content Layout */
.main-content-wrapper {
	display: flex;
	gap: 0;
	min-height: calc(100vh - 200px);
	overflow: hidden;
}

/* Timeline Section */
.timeline-section {
	flex: 1;
	transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	min-width: 0;
}

.timeline-section.timeline-expanded {
	max-width: 800px;
	margin: 0 auto;
}

.timeline-container {
	transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.timeline-centered {
	max-width: 800px;
	margin: 0 auto;
}

/* Configuration Section */
.config-section {
	width: 0;
	max-width: 480px;
	overflow: hidden;
	transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
	background-color: #ffffff;
	border-radius: 12px;
	position: relative;
}

.config-section.config-visible {
	width: 480px;
}

.config-section::before {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	width: 2px;
	height: 100%;
	background: linear-gradient(to bottom, transparent, #3b82f6, transparent);
	opacity: 0;
	transition: opacity 0.3s ease 0.2s;
}

.config-section.config-visible::before {
	opacity: 1;
}

.timeline-header {
	padding: 20px 20px 0 20px;
	margin-bottom: 20px;
}

.add-item-btn {
	border-radius: 8px;
	font-weight: 500;
	padding: 8px 16px;
}

/* Add Item Dropdown */
.add-item-dropdown {
	margin-bottom: 16px;
}

.dropdown-menu {
	border-radius: 8px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	border: 1px solid #e5e7eb;
	padding: 8px 0;
}

.dropdown-item {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	font-size: 0.95rem;
	color: #374151;
	border: none;
	background: none;
	width: 100%;
	text-align: left;
	transition: background-color 0.2s ease;
}

.dropdown-item:hover {
	background-color: #f3f4f6;
}

/* Timeline Styles - Based on Hand-drawn Design */
.sequence-timeline {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px;
}

/* Timeline Start Trigger */
.timeline-start {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 20px;
}

.timeline-trigger {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 2px solid #374151;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.2rem;
	color: #374151;
	margin-bottom: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.timeline-trigger:hover {
	background-color: #f8f9fa;
	border-color: #6c757d;
}

.timeline-trigger.active {
	border-color: #3b82f6;
	background-color: #eff6ff;
	color: #3b82f6;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.timeline-start-info {
	text-align: center;
	max-width: 280px;
	margin-bottom: 16px;
}

.start-campaign-name {
	font-weight: 600;
	font-size: 1rem;
	color: #374151;
	margin-bottom: 4px;
}

.start-campaign-description {
	font-size: 0.85rem;
	color: #6b7280;
	line-height: 1.4;
}

/* Timeline Connector Lines */
.timeline-connector {
	width: 2px;
	height: 40px;
	background-color: #374151;
	margin: 10px 0;
}

/* Timeline Items */
.timeline-items {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
}

.timeline-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	margin-bottom: 0;
}

/* Timeline Sequence Cards */
.timeline-sequence-card {
	background-color: #ffffff;
	border: 2px solid #374151;
	border-radius: 12px;
	padding: 16px;
	min-width: 200px;
	max-width: 500px;
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
	position: relative;
}

.timeline-sequence-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	transform: translateY(-2px);
}

.timeline-sequence-card.active {
	border-color: #3b82f6;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.timeline-sequence-card.email-node {
	border-color: #3b82f6;
}

.timeline-sequence-card.postcard-node {
	border-color: #06b6d4;
}

.timeline-sequence-card.letter-node {
	border-color: #dc2626;
}

/* Pulsing animations for different card types */
.timeline-sequence-card.email-node {
	animation: emailPulse 2s infinite;
}

.timeline-sequence-card.postcard-node {
	animation: postcardPulse 2s infinite;
}

.timeline-sequence-card.letter-node {
	animation: letterPulse 2s infinite;
}

@keyframes emailPulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
		border-color: #3b82f6;
	}
	50% {
		box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
		border-color: #60a5fa;
	}
}

@keyframes postcardPulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(6, 182, 212, 0.4);
		border-color: #06b6d4;
	}
	50% {
		box-shadow: 0 0 0 8px rgba(6, 182, 212, 0);
		border-color: #22d3ee;
	}
}

@keyframes letterPulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
		border-color: #dc2626;
	}
	50% {
		box-shadow: 0 0 0 8px rgba(220, 38, 38, 0);
		border-color: #f87171;
	}
}

/* Card Icon */
.card-icon {
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	background-color: #f3f4f6;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.1rem;
	color: #6b7280;
}

.timeline-sequence-card.email-node .card-icon {
	background-color: #eff6ff;
	color: #3b82f6;
}

.timeline-sequence-card.postcard-node .card-icon {
	background-color: #f0f9ff;
	color: #06b6d4;
}

.timeline-sequence-card.letter-node .card-icon {
	background-color: #fef2f2;
	color: #dc2626;
}

/* Card Content */
.card-content {
	flex-grow: 1;
}

.card-title {
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 4px;
	font-size: 1rem;
}

.card-subtitle {
	font-size: 0.85rem;
	color: #6b7280;
}

/* Card Actions */
.card-actions {
	position: absolute;
	bottom: -15px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 4px;
	opacity: 0;
	transition: opacity 0.2s ease;
	background-color: #ffffff;
	border-radius: 20px;
	padding: 4px 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	border: 1px solid #e5e7eb;
}

.timeline-sequence-card:hover .card-actions {
	opacity: 1;
}

.btn-xs {
	padding: 4px 6px;
	font-size: 0.7rem;
	border-radius: 4px;
	min-width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.card-action-btn {
	width: 20px;
	height: 20px;
	min-width: 20px;
	padding: 0;
	border-radius: 50%;
	font-size: 0.6rem;
}

/* Add Step Section */
.timeline-add-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
}

.add-step-circle {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 2px dashed #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	font-size: 1.2rem;
	color: #9ca3af;
	margin: 10px 0;
}

.add-step-circle:hover {
	border-color: #3b82f6;
	border-style: solid;
	background-color: #f8f9ff;
	color: #3b82f6;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.add-step-circle.active {
	border-color: #3b82f6;
	border-style: solid;
	background-color: #3b82f6;
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Add Item Dropdown */
.add-item-dropdown {
	position: absolute;
	top: 50%;
	left: calc(100% + 15px);
	transform: translateY(-50%);
	z-index: 10;
}

.dropdown-options {
	background-color: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	padding: 8px 0;
	min-width: 160px;
}

.dropdown-option {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	font-size: 0.9rem;
	color: #374151;
	border: none;
	background: none;
	width: 100%;
	text-align: left;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.dropdown-option:hover {
	background-color: #f3f4f6;
}

/* Timeline End */
.timeline-end {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 20px;
}

.timeline-end-circle {
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 2px solid #374151;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	color: #374151;
	font-size: 0.9rem;
}

/* Configuration Container */
.item-configuration-container {
	border-radius: 0;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	height: calc(100vh - 200px);
	padding: 20px;
	min-width: 480px;
	opacity: 0;
	transform: translateX(-20px);
	transition: all 0.3s ease 0.2s;
	position: relative;
}

.config-section.config-visible .item-configuration-container {
	opacity: 1;
	transform: translateX(0);
	box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
}

.no-selection {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
}

.no-selection-content {
	text-align: center;
	color: #6b7280;
}

.item-config h4 {
	font-size: 1.2rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 8px;
	text-transform: capitalize;
}

.config-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid #e5e7eb;
}

.config-header h4 {
	margin-bottom: 0;
}

.config-actions {
	display: flex;
	align-items: center;
	gap: 12px;
}

.item-type-badge {
	display: flex;
	align-items: center;
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 0.8rem;
	font-weight: 500;
	border: 1px solid #e5e7eb;
}

.item-type-badge.email-node {
	background-color: #eff6ff;
	color: #1e40af;
	border-color: #3b82f6;
}

.item-type-badge.postcard-node {
	background-color: #f0f9ff;
	color: #0c4a6e;
	border-color: #06b6d4;
}

.item-type-badge.letter-node {
	background-color: #fef2f2;
	color: #b91c1c;
	border-color: #dc2626;
}

.campaign-badge {
	background-color: #f1f5f9;
	color: #475569;
	border-color: #64748b;
}

.config-form {
	max-width: 500px;
}

.config-placeholder {
	background-color: #f9fafb;
	border: 1px dashed #d1d5db;
	border-radius: 8px;
	padding: 24px;
	text-align: center;
	margin-top: 24px;
}

/* Upload Placeholders */
.upload-placeholder {
	background-color: #f8f9fa;
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	padding: 20px;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #6c757d;
}

.upload-placeholder:hover {
	border-color: #3b82f6;
	background-color: #f8f9ff;
	color: #3b82f6;
}

.pdf-upload-section {
	border-radius: 8px;
	overflow: hidden;
}

.uploaded-file-info {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 6px;
}

/* Postcard Image Upload */
.postcard-image-upload {
	border-radius: 8px;
	overflow: hidden;
	min-height: 120px;
}

.uploaded-image-preview {
	position: relative;
	display: inline-block;
}

.img-preview {
	max-width: 100%;
	max-height: 120px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.uploaded-image-preview .btn {
	position: absolute;
	top: -8px;
	right: -8px;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>
